import { useCameraPermission } from '@/stores/permissions/camera-permission.store';
import {
  CapacitorBarcodeScanner,
  CapacitorBarcodeScannerScanOrientation,
  CapacitorBarcodeScannerTypeHint,
} from '@capacitor/barcode-scanner';
import { SoundIds, useSound } from './use-sound';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import { isPlatform } from '@ionic/vue';
import { useUser } from '@/stores/user.store';
import { usePermissionAlert } from './use-permission-alert';

type BarcodeResult =
  | { success: true; scanned: number; errorMsg?: undefined }
  | { success: false; scanned: undefined; errorMsg?: string };

export function useBarcodeScanner(hint: CapacitorBarcodeScannerTypeHint = CapacitorBarcodeScannerTypeHint.CODE_128) {
  const user = useUser();
  const cameraPermission = useCameraPermission();
  const { showAlert } = usePermissionAlert('Camera');
  const { play } = useSound();

  async function scan(): Promise<BarcodeResult> {
    await cameraPermission.prompt();
    if (cameraPermission.ready) {
      try {
        const res = await CapacitorBarcodeScanner.scanBarcode({
          hint,
          web: { showCameraSelection: true },
          scanOrientation: CapacitorBarcodeScannerScanOrientation.PORTRAIT,
        });
        const scanned = parseInt(res.ScanResult, 16);
        if (user.barcodeSoundsEnabled) {
          play(user.userId && user.surpriseMeEnabled ? SoundIds.HOOYAH : SoundIds.BEEP);
        }
        if (isPlatform('hybrid')) {
          Haptics.impact({ style: ImpactStyle.Light });
        }
        return {
          success: true,
          scanned,
        };
      } catch (e) {
        if ((e as Error).message?.includes('cancelled')) {
          return {
            success: false,
            scanned: undefined,
          };
        }
        return {
          success: false,
          scanned: undefined,
          errorMsg: 'Unable to scan barcode',
        };
      }
    } else {
      showAlert();
      return {
        success: false,
        scanned: undefined,
        errorMsg: 'Access must be granted to the Camera in order to use the Barcode Scanner',
      };
    }
  }

  return {
    scan,
  };
}
